import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TableModule } from '@openng/optimus-ui/table';
import { SortEvent } from '@openng/optimus-ui/api';
import { SignStatusBadgeComponent } from '@zayan-erp/shared';
import { DatePipe } from '@angular/common';
import { PhoneNumbersDisplayComponent } from '@zayan-erp/shared';
import { LotInfoPopupComponent } from '@zayan-erp/home-building';
import { DealLite } from '../../models/deal.models';

@Component({
  selector: 'sales-deals-table',
  standalone: true,
  imports: [TableModule, SignStatusBadgeComponent, DatePipe, PhoneNumbersDisplayComponent, LotInfoPopupComponent],
  templateUrl: './deals-table.component.html',
  styleUrl: './deals-table.component.scss'
})
export class DealsTableComponent {
  deals = input.required<DealLite[]>();
  actionsLinkType = input<'prospect' | 'deal'>('prospect');
  recentDealIds = input<string[]>([]);

  recentDeals = computed(() => {
    const byId = new Map(this.deals().map(d => [d.dealId, d]));
    return this.recentDealIds()
      .map(id => byId.get(id))
      .filter((d): d is DealLite => !!d);
  });

  otherDeals = computed(() => {
    const recentIds = new Set(this.recentDeals().map(d => d.dealId));
    return this.deals().filter(d => !recentIds.has(d.dealId));
  });

  rows = computed(() => [...this.recentDeals(), ...this.otherDeals()]);

  readonly #router = inject(Router);
  getStatusClass(status: string): string {
    if (!status) return 'badge bg-secondary-subtle fs-12 fw-500 text-secondary';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('selection')) {
      return 'badge bg-success-subtle fs-12 fw-500 text-success-emphasis';
    } else if (statusLower.includes('contract')) {
      return 'badge bg-info-subtle fs-12 fw-500 text-info';
    } else if (statusLower.includes('pending')) {
      return 'badge bg-warning-subtle fs-12 fw-500 text-warning';
    } else if (statusLower.includes('cancel')) {
      return 'badge bg-danger-subtle fs-12 fw-500 text-danger';
    }
    return 'badge bg-secondary-subtle fs-12 fw-500 text-secondary';
  }

  customSort(event: SortEvent): void {
    const field = event.field;
    const order = event.order ?? 1;
    const data = event.data as DealLite[] | undefined;
    if (!data) return;

    const recentOrder = this.recentDeals().map(d => d.dealId);
    const recent: DealLite[] = [];
    const others: DealLite[] = [];
    for (const deal of data) {
      if (recentOrder.includes(deal.dealId)) recent.push(deal);
      else others.push(deal);
    }

    recent.sort((a, b) => recentOrder.indexOf(a.dealId) - recentOrder.indexOf(b.dealId));
    others.sort((a, b) => this.#compareDeals(a, b, field, order));
    data.splice(0, data.length, ...recent, ...others);
  }

  dealCountLabel(count: number): string {
    return `${count} ${count === 1 ? 'deal' : 'deals'}`;
  }

  #compareDeals(a: DealLite, b: DealLite, field: string | undefined, order: number): number {
    if (field === 'contractDate' || field === 'targetClosingDate') {
      return this.#compareDates(a, b, field, order);
    }

    let valueA: string;
    let valueB: string;

    if (field === 'modelElevation') {
      valueA = `${a.model?.name ?? ''} ${a.elevation?.name ?? ''}`.toLowerCase();
      valueB = `${b.model?.name ?? ''} ${b.elevation?.name ?? ''}`.toLowerCase();
    } else {
      valueA = String(this.#resolveField(a as unknown as Record<string, unknown>, field) ?? '').toLowerCase();
      valueB = String(this.#resolveField(b as unknown as Record<string, unknown>, field) ?? '').toLowerCase();
    }

    return order * valueA.localeCompare(valueB);
  }

  #compareDates(a: DealLite, b: DealLite, field: string, order: number): number {
    const rawA = this.#resolveField(a as unknown as Record<string, unknown>, field) as string | null;
    const rawB = this.#resolveField(b as unknown as Record<string, unknown>, field) as string | null;
    const timeA = rawA ? new Date(rawA).getTime() : null;
    const timeB = rawB ? new Date(rawB).getTime() : null;

    if (timeA === null && timeB === null) return 0;
    if (timeA === null) return 1;
    if (timeB === null) return -1;
    return order * (timeA - timeB);
  }

  #resolveField(obj: Record<string, unknown>, path?: string): unknown {
    if (!path) return obj;
    return path.split('.').reduce<unknown>((o, key) => (o as Record<string, unknown>)?.[key], obj);
  }

  onRowClick(event: MouseEvent, deal: DealLite): void {
    const target = event.target as HTMLElement;
    if (target.closest('app-project-info-popup') || target.closest('app-lot-info-popup')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const commands = this.actionsLinkType() === 'deal' ? ['/sales/aps', deal.dealId] : [deal.prospect.id];
    // const commands = ['/sales/aps', deal.dealId];
    const urlTree = this.#router.createUrlTree(commands, { queryParamsHandling: 'preserve' });
    if (event.ctrlKey || event.metaKey) {
      window.open(this.#router.serializeUrl(urlTree), '_blank');
    } else {
      if (this.actionsLinkType() === 'deal') {
        this.#router.navigate(commands, { queryParamsHandling: 'preserve' });
      } else {
        this.#router.navigateByUrl(urlTree);
      }
    }
  }
}
